import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { listTemplates } from '@/api/documents';
import { lexApi } from '@/api/client';
import { downloadDocument } from '@/api/documents';
import { replaceDocumentWithDocx } from '@/lib/officeDoc';
import { Button } from '@/components/Button';

const fetchTemplateBlob = async (templateId: string): Promise<ArrayBuffer> => {
  const res = await lexApi.get(
    `/office-addin/contract-templates/${templateId}/render`,
    { responseType: 'arraybuffer' },
  );
  return res.data as ArrayBuffer;
};

export function TemplatesPane() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: listTemplates,
  });

  const onInsert = async (templateId: string) => {
    let buf: ArrayBuffer;
    try {
      buf = await fetchTemplateBlob(templateId);
    } catch {
      buf = await downloadDocument(templateId);
    }
    await replaceDocumentWithDocx(buf);
  };

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div className="flex-1 space-y-2 overflow-y-auto">
        {isLoading ? (
          <p className="text-xs text-lex-slate-500">{t('common.loading')}</p>
        ) : !data?.length ? (
          <p className="text-xs text-lex-slate-500">{t('templates.empty')}</p>
        ) : (
          data.map((tpl) => (
            <div
              key={tpl.id}
              className="rounded-md border border-lex-slate-200 p-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{tpl.name}</p>
                  {tpl.jurisdiction ? (
                    <p className="text-xs text-lex-slate-500">
                      {tpl.jurisdiction}
                    </p>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onInsert(tpl.id)}
                >
                  {t('templates.insert')}
                </Button>
              </div>
              {tpl.description ? (
                <p className="mt-1 line-clamp-2 text-xs text-lex-slate-500">
                  {tpl.description}
                </p>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
